package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
enum class ProfileVisibility {
    @SerialName("full") FULL,

    /** Blocked users see this: no bio, no online status, no last seen. */
    @SerialName("limited") LIMITED,

    @SerialName("unknown") UNKNOWN,
}

@Serializable
enum class UserRole {
    @SerialName("user") USER,
    @SerialName("moderator") MODERATOR,
    @SerialName("admin") ADMIN,
    @SerialName("unknown") UNKNOWN,
}

/** `UserPublic`. Note there is no `email` here, and there must never be. */
@Serializable
data class UserPublic(
    @SerialName("id") val id: Uuid,
    @SerialName("username") val username: String,
    @SerialName("display_name") val displayName: String,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("bio") val bio: String? = null,
    @SerialName("date_of_birth") val dateOfBirth: String? = null,
    @SerialName("show_birth_year") val showBirthYear: Boolean = true,
    @SerialName("location") val location: String? = null,
    /** True while the app is foregrounded, with a 60-second disconnect grace period. */
    @SerialName("is_online") val isOnline: Boolean? = null,
    @SerialName("last_seen_at") val lastSeenAt: Timestamp? = null,
    @SerialName("created_at") val createdAt: Timestamp,
    @SerialName("profile_url") val profileUrl: String? = null,
    @SerialName("is_blocked_by_me") val isBlockedByMe: Boolean = false,
    @SerialName("profile_visibility") val profileVisibility: ProfileVisibility = ProfileVisibility.UNKNOWN,
    /** Public, privacy-safe projection: true only for an active Black entitlement with badge enabled. */
    @SerialName("has_black_badge") val hasBlackBadge: Boolean = false,
)

/**
 * `UserPrivate` -- the contract composes this with `allOf: [UserPublic, {...}]`.
 * kotlinx.serialization has no inheritance-based composition for data classes, so the public
 * fields are repeated here rather than nested. The JSON shape is identical, which is what the
 * contract specifies; a nested object would NOT match.
 */
@Serializable
data class UserPrivate(
    @SerialName("id") val id: Uuid,
    @SerialName("username") val username: String,
    @SerialName("display_name") val displayName: String,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("bio") val bio: String? = null,
    @SerialName("date_of_birth") val dateOfBirth: String? = null,
    @SerialName("show_birth_year") val showBirthYear: Boolean = true,
    @SerialName("location") val location: String? = null,
    @SerialName("is_online") val isOnline: Boolean? = null,
    @SerialName("last_seen_at") val lastSeenAt: Timestamp? = null,
    @SerialName("created_at") val createdAt: Timestamp,
    @SerialName("profile_url") val profileUrl: String? = null,
    @SerialName("is_blocked_by_me") val isBlockedByMe: Boolean = false,
    @SerialName("profile_visibility") val profileVisibility: ProfileVisibility = ProfileVisibility.UNKNOWN,
    @SerialName("has_black_badge") val hasBlackBadge: Boolean = false,
    // UserPrivate additions
    @SerialName("email") val email: String,
    @SerialName("email_verified") val emailVerified: Boolean,
    @SerialName("role") val role: UserRole,
    @SerialName("version") val version: ResourceVersion? = null,
    @SerialName("username_change_available_at") val usernameChangeAvailableAt: Timestamp? = null,
)

/**
 * `DeletedUser`. Messages from a deleted account keep showing under this rather than
 * disappearing -- see `IMPLEMENTATION_NOTES.md` §1, which forbids cascade-deleting messages.
 */
@Serializable
data class DeletedUser(
    @SerialName("id") val id: Uuid? = null,
    @SerialName("username") val username: String? = null,
    @SerialName("display_name") val displayName: String = DELETED_ACCOUNT_NAME,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("is_deleted") val isDeleted: Boolean = true,
) {
    companion object {
        const val DELETED_ACCOUNT_NAME = "Deleted Account"
    }
}
